/*
 * Planner browser contents belong to a verified account and an authorized
 * advertiser. Callers obtain that scope from PlannerSessionBoundary.
 *
 * The old single-slot, v1, v2 and advertiser-scoped keys have no established
 * owner. They are deliberately never read, copied, marked saved or removed.
 * Recovery of those bytes requires a separate ownership decision.
 *
 * Keep byte-identical in Cortex and Quantum.
 */

const normalizeAccountId = (value) => {
  const id = String(value ?? "").trim();
  return /^[A-Za-z0-9_-]{1,128}$/.test(id) ? id : "";
};

const normalizeAdvertiserId = (value) => {
  const id = String(value ?? "").trim();
  const number = Number(id);
  return /^\d+$/.test(id) && Number.isSafeInteger(number) && number > 0 ? String(number) : "";
};

const parse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const browserStorage = () => {
  try { return typeof window === "undefined" ? null : window.localStorage; } catch { return null; }
};

let writerSequence = 0;

const scopeError = () => new Error("Planner browser storage requires the current verified account and advertiser.");

export const createPlannerBrowserStorage = ({
  portal,
  accountId,
  advertiserId,
  isCurrent,
  normalizeForm = (form) => form,
  storage = browserStorage(),
} = {}) => {
  const owner = Object.freeze({
    portal: portal === "client" || portal === "superadmin" ? portal : "",
    accountId: normalizeAccountId(accountId),
    advertiserId: normalizeAdvertiserId(advertiserId),
  });
  const hasScope = Boolean(owner.portal && owner.accountId && owner.advertiserId);
  const logicalPrefix = `sherlocMediaPlanner_${owner.portal}_`;
  const namespace = `${logicalPrefix}owned_v1_acct_${owner.accountId}_adv_${owner.advertiserId}__`;
  const advertiserKey = `id_${owner.advertiserId}`;
  const current = () => {
    try {
      return Boolean(hasScope && storage && typeof isCurrent === "function" && isCurrent());
    } catch {
      return false;
    }
  };

  const suffixFor = (logicalKey) => {
    if (!hasScope || typeof logicalKey !== "string" || !logicalKey.startsWith(logicalPrefix)) return "";
    const suffix = logicalKey.slice(logicalPrefix.length);
    if (
      suffix === "planner_index_v2" ||
      suffix === `active_planner_id_v2_${advertiserKey}` ||
      suffix === `chosen_plan_v1_acct_${owner.accountId}_${advertiserKey}`
    ) return suffix;
    if (/^planner_state_v2_[A-Za-z0-9_.:-]{1,160}$/.test(suffix)) return suffix;
    if (/^planner_pending_save_v1_[A-Za-z0-9_.:-]{1,160}__[0-9a-f-]{36}$/i.test(suffix)) return suffix;
    return "";
  };

  const validValue = (suffix, value) => {
    if (typeof value !== "string") return false;
    if (suffix === "planner_index_v2") {
      const entries = parse(value);
      return Array.isArray(entries) && entries.every((entry) =>
        entry && typeof entry === "object" &&
        entry.advertiserKey === advertiserKey &&
        (!entry.advertiserId || normalizeAdvertiserId(entry.advertiserId) === owner.advertiserId)
      );
    }
    if (suffix.startsWith("planner_state_v2_")) {
      const copy = parse(value);
      return Boolean(
        copy && typeof copy === "object" && !Array.isArray(copy) &&
        copy.formData && normalizeAdvertiserId(copy.formData.advertiserId) === owner.advertiserId
      );
    }
    return true;
  };

  const keyFor = (logicalKey) => {
    const suffix = suffixFor(logicalKey);
    return suffix ? `${namespace}${suffix}` : "";
  };

  const envelopeFor = (value, extra = {}) => JSON.stringify({ version: 1, owner, value, ...extra });
  const readEnvelope = (suffix) => {
    let envelope;
    try { envelope = parse(storage.getItem(`${namespace}${suffix}`)); } catch { return null; }
    if (envelope?.version !== 1 || envelope?.owner?.portal !== owner.portal ||
      envelope?.owner?.accountId !== owner.accountId || envelope?.owner?.advertiserId !== owner.advertiserId ||
      !validValue(suffix, envelope?.value)) return null;
    return envelope;
  };
  // Metadata changes are not a distinct input version. All remaining fields,
  // including unknown content fields, participate in the comparison.
  const contents = (raw) => {
    const value = parse(raw);
    if (!value) return "";
    const { serverDraftId, serverSavedAt, unsavedChanges, supersededServerDraftId, plannerWriteVersion, plannerReceiptId, plannerRecoveryRequired, plannerExplicitSaveRequired, awaitingFirstExplicitSave, ...content } = value;
    let formData = content.formData;
    try { formData = normalizeForm(formData); } catch { /* Compare conservatively. */ }
    const normalized = {
      ...content, formData,
      strategy: content.strategy ?? "",
      creativeRecommendations: content.creativeRecommendations ?? content.creativeVariations ?? [],
      creativeVariations: content.creativeVariations ?? content.creativeRecommendations ?? [],
      creativeGenerationMeta: content.creativeGenerationMeta ?? null,
    };
    const ordered = (item) => Array.isArray(item) ? item.map(ordered)
      : item && typeof item === "object" ? Object.fromEntries(Object.keys(item).sort().map((key) => [key, ordered(item[key])])) : item;
    return JSON.stringify(ordered(normalized));
  };
  const writerId = `${Date.now().toString(36)}_${(++writerSequence).toString(36)}_${Math.random().toString(36).slice(2)}`;
  const writerCopies = new Map();
  const statePrefix = "planner_state_v2_";
  const projectedIndex = (raw) => {
    const entries = parse(raw) || [];
    // Discover only this verified owner's namespace. Never inspect legacy
    // namespaces. Index writes can race across tabs, so an index alone cannot
    // be the sole record of a preserved version.
    const copies = [];
    try {
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (!key?.startsWith(`${namespace}${statePrefix}writer_`)) continue;
        const suffix = key.slice(namespace.length);
        const envelope = readEnvelope(suffix);
        if (!envelope?.preservedFrom || !suffixFor(`${logicalPrefix}${envelope.preservedFrom}`)) continue;
        const canonical = readEnvelope(envelope.preservedFrom);
        copies.push({ suffix, envelope, visible: !envelope.explicitlyRemoved && !envelope.confirmedSaved && contents(envelope.value) !== contents(canonical?.value) });
      }
    } catch { /* Existing indexed copies remain usable if enumeration fails. */ }
    const copyIds = new Set(copies.map(({suffix}) => suffix.slice(statePrefix.length)));
    const result = entries.filter((entry) => !copyIds.has(entry.id));
    const listedContents = new Set(result.map((entry) => contents(readEnvelope(`${statePrefix}${entry.id}`)?.value)).filter(Boolean));
    for (const {suffix, envelope, visible} of copies) {
      const fingerprint = contents(envelope.value);
      if (!visible || listedContents.has(fingerprint)) continue;
      listedContents.add(fingerprint);
      const copy = parse(envelope.value);
      result.push({
        id: suffix.slice(statePrefix.length),
        title: [copy.formData.advertiser, copy.formData.campaignName].filter(Boolean).join(" — ") || "Unsaved plan",
        advertiserKey, advertiserId: owner.advertiserId,
        advertiserName: copy.formData.advertiser || "",
        updatedAt: envelope.preservedAt,
      });
    }
    return raw === null && !result.length ? null : JSON.stringify(result);
  };

  return Object.freeze({
    owner,
    keyFor,
    listPendingSaves(planId) {
      if (!current()) throw scopeError();
      if (!/^[A-Za-z0-9_.:-]{1,160}$/.test(String(planId))) throw scopeError();
      const prefix = `planner_pending_save_v1_${planId}__`;
      const records = [];
      // Enumerate only this owner. A corrupt owned record must block new
      // requests rather than hiding an operation whose outcome is unknown.
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (!key?.startsWith(`${namespace}${prefix}`)) continue;
        // Plan IDs may themselves contain "__". Match the complete identity
        // before the final request separator, never another plan's prefix.
        if (key.slice(namespace.length, key.lastIndexOf("__")) !== `planner_pending_save_v1_${planId}`) continue;
        const suffix = key.slice(namespace.length);
        const envelope = readEnvelope(suffix);
        records.push({ key: `${logicalPrefix}${suffix}`, value: envelope?.value ?? null });
      }
      if (!current()) throw scopeError();
      return records;
    },
    getItem(logicalKey) {
      if (!current()) return null;
      const suffix = suffixFor(logicalKey);
      if (!suffix) return null;
      const envelope = readEnvelope(suffix);
      return suffix === "planner_index_v2" ? projectedIndex(envelope?.value ?? null) : envelope?.value ?? null;
    },
    setItem(logicalKey, rawValue) {
      const suffix = suffixFor(logicalKey);
      const value = String(rawValue);
      if (!current() || !suffix || !validValue(suffix, value)) throw scopeError();
      if (suffix.startsWith(statePrefix)) {
        const copy = parse(value);
        if (copy.unsavedChanges !== false || copy.plannerRecoveryRequired || copy.plannerExplicitSaveRequired) {
          // Each mounted writer owns its own latest unsaved copy. Write it
          // first: simultaneous tabs can never overwrite one another's copy,
          // even when their canonical writes interleave. No CAS is assumed.
          let keptSuffix = writerCopies.get(suffix);
          if (!keptSuffix) {
            keptSuffix = `${statePrefix}writer_${writerId}_${writerCopies.size}`;
            writerCopies.set(suffix, keptSuffix);
          }
          const keptValue = JSON.stringify({ ...copy, serverDraftId: null, serverSavedAt: null,
            unsavedChanges: true, supersededServerDraftId: copy.serverDraftId || copy.supersededServerDraftId || null });
          storage.setItem(`${namespace}${keptSuffix}`, envelopeFor(keptValue, {
            preservedFrom: suffix, preservedAt: new Date().toISOString(),
          }));
        } else {
          const keptSuffix = writerCopies.get(suffix);
          const kept = keptSuffix ? readEnvelope(keptSuffix) : null;
          // A receipt confirms only the matching latest copy this writer made.
          // Keep its bytes, and never relabel a different or another writer's
          // pending version as saved.
          if (kept && contents(kept.value) === contents(value)) {
            storage.setItem(`${namespace}${keptSuffix}`, JSON.stringify({ ...kept, confirmedSaved: true }));
          }
        }
      }
      // A preservation/quota failure aborts before canonical overwrite. No
      // fallback ever touches ownerless contents. Preserved copies are never
      // automatically deleted or adopted from another owner.
      storage.setItem(`${namespace}${suffix}`, envelopeFor(value));
    },
    removeItem(logicalKey) {
      const key = keyFor(logicalKey);
      if (!current() || !key) throw scopeError();
      const suffix = suffixFor(logicalKey);
      const removed = readEnvelope(suffix);
      if (removed && suffix.startsWith(statePrefix)) {
        // The existing explicit Delete action removes a logical local plan.
        // Keep preservation bytes, but do not resurrect equal copies as new
        // choices. Distinct contents and all ownerless keys remain untouched.
        const keys = [];
        for (let i = 0; i < storage.length; i += 1) keys.push(storage.key(i));
        for (const candidate of keys) {
          if (!candidate?.startsWith(`${namespace}${statePrefix}writer_`)) continue;
          const preserved = readEnvelope(candidate.slice(namespace.length));
          if (preserved?.preservedFrom === suffix && contents(preserved.value) === contents(removed.value)) {
            storage.setItem(candidate, JSON.stringify({ ...preserved, explicitlyRemoved: true }));
          }
        }
      }
      storage.removeItem(key);
    },
  });
};
